/**
 * Templates edit route.
 *
 * Path: /_authenticated/templates/:templateId
 * Layout (MainLayout, sidebar) inherited from _authenticated parent.
 *
 * Hydrates the TemplateBuilder from the API. View-only for templates the
 * caller doesn't own (clinic-shared and admin defaults) -- the builder
 * surfaces a banner and disables Save in that case.
 */

import { useEffect, useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';

import { TemplateBuilder } from '../../components/templates/TemplateBuilder';
import { MainLayoutContent, MainLayoutHeader } from '../../components/layout/MainLayout';
import { Icon } from '../../components/ui/Icon';
import { SidebarNavIcons } from '../../components/ui/Icon/generalIcons';
import { getUserTemplate } from '../../services/templates.service';
import type { UserTemplate } from '../../types/template.types';

const TemplatesEditRouteComponent = () => {
  const { templateId } = useParams({ from: '/_authenticated/templates/$templateId' });
  const numericId = Number(templateId);

  const [template, setTemplate] = useState<UserTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (Number.isNaN(numericId)) {
      setError('Invalid template id.');
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    getUserTemplate(numericId)
      .then((data) => {
        if (active) {
          setTemplate(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (active) {
          setError(err instanceof Error ? err.message : 'Failed to load template.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [numericId]);

  return (
    <MainLayoutContent>
      <MainLayoutHeader
        title={template?.name ?? 'Template'}
        subtitle={
          template?.scope === 'own'
            ? 'Edit your custom template.'
            : 'Read-only view. Use Duplicate from the gallery to fork it before editing.'
        }
        breadcrumbs={[
          { label: 'Home', href: '/home', icon: <Icon icon={SidebarNavIcons.Home} size="sm" /> },
          {
            label: 'Templates',
            href: '/templates',
            icon: <Icon icon={SidebarNavIcons.Templates} size="sm" />,
          },
          { label: template?.name ?? '...' },
        ]}
      />
      {loading ? (
        <div className="rounded border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Loading template...
        </div>
      ) : error ? (
        <div className="rounded border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : template ? (
        <TemplateBuilder initial={template} />
      ) : null}
    </MainLayoutContent>
  );
};

export const Route = createFileRoute('/_authenticated/templates/$templateId')({
  component: TemplatesEditRouteComponent,
});
